import TabsWithOutlet from '../../layouts/components/TabsWithOutlet';
import React from 'react';
import { Navigate, Route, Routes, useParams, useNavigate } from 'react-router-dom';
import { DataManagementTab } from '@shared/components/data-management';
import { useConfirmation } from '@shared/components/confirmation';
import { useRemoveInstituteAdmin } from './hooks/useInstituteAdmins';
import { createSubjectsConfig } from './config/subjectsConfig';
import { createInstituteAdminsConfig } from './config/instituteAdminsConfig';
import { DomainRouteParams, RemoveInstituteAdminParams } from './types';
import { Box, Typography } from '@mui/material';
import { useBreadcrumbManager } from '../../hooks/useBreadcrumbManager';
import { SubjectRoutes } from '../subject';
import { useAuthStore } from '../authentication';
import { UserRole } from '../authentication/types';
import { useHasRole } from '../../hooks/useActiveRole';
import { useContextStore } from '../../stores/contextStore';

// Institute admins component using the enhanced config
const InstituteAdminsTab: React.FC = () => {
  const { tenantId, domainId } = useParams<DomainRouteParams>();
  const { confirm } = useConfirmation();
  const removeInstituteAdminMutation = useRemoveInstituteAdmin(tenantId!, domainId!);

  // Create wrapper that matches expected signature
  const removeInstituteAdminWrapper = {
    mutateAsync: async (params: RemoveInstituteAdminParams) => {
      await removeInstituteAdminMutation.mutateAsync(params);
      return;
    },
  };

  const instituteAdminsConfig = createInstituteAdminsConfig(
    tenantId!,
    domainId!,
    removeInstituteAdminWrapper,
    confirm
  );

  return <DataManagementTab config={instituteAdminsConfig} />;
};

// Main domain routes component
export const DomainRoutes: React.FC = () => {
  const { tenantId, domainId } = useParams<DomainRouteParams>();
  const navigate = useNavigate();

  // Initialize breadcrumb manager to automatically fetch and set domain/institute names
  useBreadcrumbManager();

  // Get auth store values and context store for system admin context management
  const { currentTenant } = useAuthStore();
  const { setActiveTenantId, setActiveDomainId } = useContextStore();

  // System admins can access any tenant/domain, institute admins are restricted to their own
  const isSystemAdmin = useHasRole(UserRole.SYSTEM_ADMIN);

  // For system admins, set the context to match the tenant and domain they're viewing
  React.useEffect(() => {
    if (isSystemAdmin && tenantId && domainId) {
      // Set tenant context if different from current
      if (tenantId !== currentTenant?.id) {
        setActiveTenantId(tenantId);
      }
      // Always set domain context for system admins
      setActiveDomainId(domainId);
    }
  }, [isSystemAdmin, tenantId, domainId, currentTenant?.id, setActiveTenantId, setActiveDomainId]);

  // Validate route parameters
  if (!tenantId || !domainId) {
    return (
      <Box sx={{ p: 3 }}>
        <Typography variant="h6" color="error">
          Invalid institute ID or university ID
        </Typography>
        <Typography variant="body2" sx={{ mt: 1 }}>
          Please select a valid institute from the list.
        </Typography>
      </Box>
    );
  }

  return (
    <Routes>
      <Route
        element={
          <TabsWithOutlet
            tabs={[
              {
                label: 'Subjects',
                href: `/app/manage/universities/${tenantId}/institutes/${domainId}/subjects`,
              },
              {
                label: 'Users',
                href: `/app/manage/universities/${tenantId}/institutes/${domainId}/users`,
              },
            ]}
          />
        }
      >
        <Route
          index
          element={
            <Navigate
              to={`/app/manage/universities/${tenantId}/institutes/${domainId}/subjects`}
              replace
            />
          }
        />
        <Route
          path="subjects"
          element={
            <DataManagementTab config={createSubjectsConfig(tenantId, domainId, navigate)} />
          }
        />
        <Route path="users" element={<InstituteAdminsTab />} />
      </Route>
      <Route path="subjects/:subjectId/*" element={<SubjectRoutes />} />
    </Routes>
  );
};
